import React, { Suspense, lazy } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { SiteProvider, useSite } from "./context/SiteContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollTopBtn from "./components/ScrollTopBtn";
import FloatingActions from "./components/FloatingActions";
import LiveChat from "./components/LiveChat";
import Top from "./components/Top";
import Loader from "./components/Loader";
import Seo from "./components/Seo";
import seo from "./data/seo";

// Pages are code-split: each loads only when visited.
const Home = lazy(() => import("./pages/Home/Home"));
const About = lazy(() => import("./pages/About/About"));
const Packages = lazy(() => import("./pages/Packages/Packages"));
const PackagesDetails = lazy(
  () => import("./pages/PackagesDetails/PackagesDetails"),
);
const Service = lazy(() => import("./pages/Service/Service"));
const ServiceDetails = lazy(
  () => import("./pages/ServiceDetails/ServiceDetails"),
);
const Doctors = lazy(() => import("./pages/Doctors/Doctors"));
const DoctorDetails = lazy(() => import("./pages/DoctorDetails/DoctorDetails"));
const DoctorsTimetable = lazy(
  () => import("./pages/DoctorsTimetable/DoctorsTimetable"),
);
const Gallery = lazy(() => import("./pages/Gallery/Gallery"));
const Blog = lazy(() => import("./pages/Blog/Blog"));
const BlogDetails = lazy(() => import("./pages/BlogDetails/BlogDetails"));
const Contact = lazy(() => import("./pages/Contact/Contact"));
const Appointment = lazy(() => import("./pages/Appointment/Appointment"));
const Search = lazy(() => import("./pages/Search/Search"));
const LegalPage = lazy(() => import("./pages/Legal/LegalPage"));
const SelfAssessment = lazy(
  () => import("./pages/SelfAssessment/SelfAssessment"),
);
const Admission = lazy(() => import("./pages/Admission/Admission"));
const Faq = lazy(() => import("./pages/Faq/Faq"));
const NotFound = lazy(() => import("./components/NotFound"));

// Detail pages set their own <Seo> from the loaded record, so `seo` is omitted for them.
const routes = [
  { path: "/", element: <Home />, seo: seo.home },
  { path: "/about", element: <About />, seo: seo.about },
  { path: "/packages", element: <Packages />, seo: seo.packages },
  { path: "/packages/package-details/:id", element: <PackagesDetails /> },
  { path: "/services", element: <Service />, seo: seo.services },
  { path: "/services/service-details/:id", element: <ServiceDetails /> },
  { path: "/doctors", element: <Doctors />, seo: seo.doctors },
  { path: "/doctors/doctor-details/:id", element: <DoctorDetails /> },
  { path: "/time-table", element: <DoctorsTimetable />, seo: seo.timetable },
  { path: "/gallery", element: <Gallery />, seo: seo.gallery },
  { path: "/blog", element: <Blog />, seo: seo.blog },
  { path: "/blog/blog-details/:id", element: <BlogDetails /> },
  { path: "/blog/:id", element: <BlogDetails /> }, // slug URLs, e.g. /blog/why-detox-alone-is-not-recovery-...
  { path: "/contact", element: <Contact />, seo: seo.contact },
  { path: "/appointment", element: <Appointment />, seo: seo.appointment },
  {
    path: "/appointment-doctor/:id",
    element: <Appointment />,
    seo: seo.appointment,
  },
  {
    path: "/self-assessment",
    element: <SelfAssessment />,
    seo: seo.assessment,
  },
  { path: "/admission", element: <Admission />, seo: seo.admission },
  { path: "/faq", element: <Faq />, seo: seo.faq },
  { path: "/search", element: <Search /> },
  { path: "/privacy-policy", element: <LegalPage slug="privacy-policy" /> },
  {
    path: "/terms-and-conditions",
    element: <LegalPage slug="terms-and-conditions" />,
  },
  { path: "*", element: <NotFound />, seo: seo.notFound },
];

// Page title + description from the site content (bundled seo.js until the backend answers)
const RouteSeo = ({ page }) => {
  const { content } = useSite();
  const meta = content.seo?.[page] || seo[page];
  return meta ? (
    <Seo title={meta.title} description={meta.description} />
  ) : null;
};

function App() {
  return (
    <SiteProvider>
      <BrowserRouter>
        <div className="wrapper">
          <Header />
          <Suspense fallback={<Loader />}>
            <Routes>
              {routes.map((r) => (
                <Route
                  key={r.path}
                  path={r.path}
                  element={
                    <>
                      {r.seo && <RouteSeo page={r.seo} />}
                      {r.element}
                    </>
                  }
                />
              ))}
            </Routes>
          </Suspense>
          <Footer />
          <Top />
          <ScrollTopBtn />
          <FloatingActions />
          <LiveChat />
        </div>
      </BrowserRouter>
    </SiteProvider>
  );
}

export default App;
